"use client";

import { useEffect } from "react";

/**
 * iOS keyboard snap-back — CluckupViewportReset ported verbatim (rename only;
 * cu operator report IMG_8958, 2026-06-10). Supply has three keyboard-summoning
 * fields inside the fixed shell (order search, reject textarea, yield input).
 *
 * When the on-screen keyboard would cover a focused input, iOS pans the WINDOW
 * itself upward to reveal it — even though the SupplyShell body has no
 * scrollable overflow. On keyboard dismissal WebKit frequently leaves that pan
 * in place: the whole app sits shifted up, with the root <body> background
 * showing as a dead band under the tab bar until something forces a relayout
 * (the canvas-paint half of the fix is supply-theme.css:133-142). This
 * listener snaps the leftover window pan back to 0 once the keyboard is
 * actually closed.
 *
 * "Keyboard closed" = the visual viewport height is back to (roughly) the
 * layout viewport height. The check also keeps us from fighting iOS while the
 * keyboard is open (input must stay revealed) or while the user is
 * pinch-zoomed (visualViewport.height shrinks with scale, so the condition
 * can't hold and we leave their pan alone).
 */

/** Pure decision — unit-tested. */
export function shouldSnapBack(args: {
  visualViewportHeight: number;
  visualViewportScale: number;
  innerHeight: number;
  scrollX: number;
  scrollY: number;
}): boolean {
  // The keyboard eats 35–45% of the screen; browser chrome / home-indicator
  // noise is a few px. A loose 80% threshold separates them cleanly (an
  // exact-match threshold missed real devices where the visual viewport sits
  // ~10px short of innerHeight even with the keyboard closed).
  const keyboardClosed = args.visualViewportHeight >= args.innerHeight * 0.8;
  // Never fight a deliberate pinch-zoom pan.
  const notZoomed = Math.abs(args.visualViewportScale - 1) < 0.02;
  const panned = args.scrollY !== 0 || args.scrollX !== 0;
  return keyboardClosed && notZoomed && panned;
}

export function SupplyViewportReset() {
  useEffect(() => {
    const vv = window.visualViewport;
    if (!vv) return;

    const settle = () => {
      if (
        shouldSnapBack({
          visualViewportHeight: vv.height,
          visualViewportScale: vv.scale,
          innerHeight: window.innerHeight,
          scrollX: window.scrollX,
          scrollY: window.scrollY,
        })
      ) {
        window.scrollTo(0, 0);
      }
    };

    // Keyboard open/close resizes the visual viewport; focusout catches the
    // dismissals that don't fire a resize until later (the timeout lets the
    // viewport finish animating before we measure).
    const onFocusOut = () => {
      window.setTimeout(settle, 80);
      window.setTimeout(settle, 350);
    };

    vv.addEventListener("resize", settle);
    window.addEventListener("focusout", onFocusOut);
    return () => {
      vv.removeEventListener("resize", settle);
      window.removeEventListener("focusout", onFocusOut);
    };
  }, []);

  return null;
}
